/**
 * Castle architecture — turned, not boxed.
 * Lathe-profiled towers, extruded battered curtain walls with a real arched
 * gate tunnel (THREE.Shape hole), a crenellated keep with corner turrets,
 * instanced merlons and arrow slits. Every builder also registers analytic
 * colliders the player controller resolves against.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createTriplanarMaterial } from './triplanar';
import { paintAshlar, paintLimewash, paintShingle, paintWood } from './textures';

export const WALL_HALF = 40;
const WALL_H = 8;
const WALL_BASE_T = 2.6;
const WALL_TOP_T = 1.7;

export interface Collider {
  kind: 'aabb' | 'cylinder' | 'ring';
  /** aabb */
  minX?: number;
  maxX?: number;
  minZ?: number;
  maxZ?: number;
  /** cylinder */
  x?: number;
  z?: number;
  r?: number;
  /** ring: annulus centred (x,z) between rIn..rOut (walkable on top).
   *  aStart/aEnd: solid angular span in degrees (0 = +Z/south, -180..180).
   *  Omit the span for a full annulus. */
  rIn?: number;
  rOut?: number;
  aStart?: number;
  aEnd?: number;
  top: number;
  bottom: number;
}

export interface CastleBuild {
  group: THREE.Group;
  colliders: Collider[];
  merlonCount: number;
  dispose(): void;
}

/** Collects stone/wood/roof geometry during the build; merges per material. */
class GeometryBag {
  stone: THREE.BufferGeometry[] = [];
  lime: THREE.BufferGeometry[] = [];
  wood: THREE.BufferGeometry[] = [];
  roof: THREE.BufferGeometry[] = [];
}

function latheProfile(points: Array<[number, number]>, segments: number): THREE.LatheGeometry {
  const v = points.map(([r, y]) => new THREE.Vector2(Math.max(0.001, r), y));
  return new THREE.LatheGeometry(v, segments);
}

interface BuildCtx {
  bag: GeometryBag;
  colliders: Collider[];
  merlonXforms: THREE.Matrix4[];
  slitXforms: THREE.Matrix4[];
  plateauY: number;
  /** Alias kept for readability inside builders. */
  plateauWY(): number;
}


function addCollider(ctx: BuildCtx, c: Collider): void {
  ctx.colliders.push(c);
}

/** One straight curtain wall run centred at `from`→`to` (XZ), with parapet. */
function buildWallRun(
  ctx: BuildCtx,
  from: [number, number],
  to: [number, number],
  opts: { h?: number; parapet?: boolean; skipMerlons?: boolean; noCollider?: boolean } = {},
): void {
  const h = opts.h ?? WALL_H;
  const dx = to[0] - from[0];
  const dz = to[1] - from[1];
  const len = Math.hypot(dx, dz);
  if (len < 0.5) return;

  // Battered cross-section (thickness dir X, height Y) swept along +Z then placed.
  const shape = new THREE.Shape();
  shape.moveTo(-WALL_BASE_T / 2, 0);
  shape.lineTo(WALL_BASE_T / 2, 0);
  shape.lineTo(WALL_TOP_T / 2, h - 0.9);
  shape.lineTo(WALL_TOP_T / 2, h);
  shape.lineTo(-WALL_TOP_T / 2, h);
  shape.lineTo(-WALL_TOP_T / 2, h - 0.9);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: len, bevelEnabled: false });
  geo.translate(0, ctx.plateauY, -len / 2);
  geo.rotateY(Math.atan2(dx, dz));
  geo.translate(from[0], 0, from[1]);
  ctx.bag.stone.push(geo);

  if (!opts.noCollider) {
    addCollider(ctx, {
      kind: 'aabb',
      minX: Math.min(from[0], to[0]) - (dx === 0 ? WALL_BASE_T / 2 : 0),
      maxX: Math.max(from[0], to[0]) + (dx === 0 ? WALL_BASE_T / 2 : 0),
      minZ: Math.min(from[1], to[1]) - (dz === 0 ? WALL_BASE_T / 2 : 0),
      maxZ: Math.max(from[1], to[1]) + (dz === 0 ? WALL_BASE_T / 2 : 0),
      top: ctx.plateauY + h,
      bottom: ctx.plateauY,
    });
  }

  if (opts.parapet !== false && !opts.skipMerlons) {
    // Parapet lip on the OUTER side (away from courtyard centre).
    const nx = dx / len;
    const nz = dz / len;
    const outerSign = from[1] > 0 || to[1] > 0 ? 1 : from[0] > 0 || to[0] > 0 ? 1 : -1;
    void outerSign;
    const px = -nz;
    const pz = nx;
    for (const side of [-1, 1]) {
      // Lips both sides — the wall-walk runs between them.
      const lipGeo = new THREE.BoxGeometry(0.45, 1.15, len);
      lipGeo.translate(px * side * (WALL_TOP_T / 2 - 0.22), ctx.plateauY + h + 0.575, len / 2);
      lipGeo.rotateY(Math.atan2(dx, dz));
      lipGeo.translate(from[0], 0, from[1]);
      ctx.bag.stone.push(lipGeo);
    }
    // Merlons along both lips, instanced later.
    const count = Math.floor(len / 2.6);
    for (let i = 0; i <= count; i += 1) {
      for (const side of [-1, 1]) {
        const t = (i / Math.max(1, count)) * len;
        const m = new THREE.Matrix4();
        m.makeRotationY(Math.atan2(dx, dz));
        m.setPosition(
          from[0] + nx * t + px * side * (WALL_TOP_T / 2 - 0.22),
          ctx.plateauY + h + 1.15 + 0.55,
          from[1] + nz * t + pz * side * (WALL_TOP_T / 2 - 0.22),
        );
        const s = new THREE.Matrix4().makeScale(1.35, 1.1, 0.5);
        m.multiply(s);
        ctx.merlonXforms.push(m);
      }
    }
  }
}

/** Round drum tower off a lathe profile: plinth, batter, string course, parapet ring. */
function buildRoundTower(
  ctx: BuildCtx,
  x: number,
  z: number,
  radius: number,
  bodyH: number,
  withCap: boolean,
): void {
  const y0 = ctx.plateauY;
  const profile: Array<[number, number]> = [
    [radius + 0.5, 0],
    [radius + 0.28, 1.1],
    [radius - 0.18, bodyH - 0.8],
    [radius + 0.16, bodyH], // string course
    [radius - 0.3, bodyH + 0.5],
    [radius + 0.05, bodyH + 1.7], // parapet band (machicolated look)
    [radius - 0.42, bodyH + 1.85],
  ];
  const geo = latheProfile(profile, 24);
  geo.translate(x, y0, z);
  ctx.bag.stone.push(geo);

  // Crenel ring on the parapet.
  const merlons = Math.max(8, Math.round((radius * 2 * Math.PI) / 2.4));
  for (let i = 0; i < merlons; i += 1) {
    const a = (i / merlons) * Math.PI * 2;
    const m = new THREE.Matrix4();
    m.makeRotationY(-a);
    m.setPosition(
      x + Math.cos(a) * (radius - 0.28),
      y0 + bodyH + 2.4,
      z + Math.sin(a) * (radius - 0.28),
    );
    m.multiply(new THREE.Matrix4().makeScale(1.15, 1.05, 0.5));
    ctx.merlonXforms.push(m);
  }

  // Arrow slits around the drum.
  const slitRows = bodyH > 10 ? [bodyH * 0.38, bodyH * 0.66] : [bodyH * 0.5];
  for (const sy of slitRows) {
    for (let i = 0; i < 8; i += 1) {
      const a = (i / 8) * Math.PI * 2 + 0.2;
      const m = new THREE.Matrix4();
      m.makeRotationY(-a);
      m.setPosition(
        x + Math.cos(a) * (radius + 0.06),
        y0 + sy,
        z + Math.sin(a) * (radius + 0.06),
      );
      m.multiply(new THREE.Matrix4().makeScale(0.34, 1.5, 0.06));
      ctx.slitXforms.push(m);
    }
  }

  if (withCap) {
    const cap = new THREE.ConeGeometry(radius + 0.55, radius * 0.95, 20);
    cap.translate(x, y0 + bodyH + 1.9 + radius * 0.48, z);
    ctx.bag.roof.push(cap);
    const pole = new THREE.CylinderGeometry(0.07, 0.07, 2.6, 6);
    pole.translate(x, y0 + bodyH + 1.9 + radius * 0.96 + 1.2, z);
    ctx.bag.wood.push(pole);
  }

  // Solid to wall-walk level, thin walkable disc above it so battlements
  // adjoin the wall-walk where they meet.
  addCollider(ctx, { kind: 'cylinder', x, z, r: radius - 0.1, top: y0 + WALL_H, bottom: y0 });
  addCollider(ctx, {
    kind: 'aabb',
    minX: x - radius + 0.6,
    maxX: x + radius - 0.6,
    minZ: z - radius + 0.6,
    maxZ: z + radius - 0.6,
    top: y0 + bodyH + 0.5,
    bottom: y0 + WALL_H,
  });
}

/**
 * Gatehouse: two flanking drums, a passage block with a real arch cut through
 * (Shape hole → true tunnel), raised portcullis, flat deck across the moat.
 */
function buildGatehouse(ctx: BuildCtx): void {
  const y0 = ctx.plateauWY();
  const gz = WALL_HALF; // south wall line
  const halfPassage = 3.1;
  const passDepth = 7;
  const passH = 12.5;

  buildRoundTower(ctx, -(halfPassage + 3.4), gz, 4.3, 11.5, true);
  buildRoundTower(ctx, halfPassage + 3.4, gz, 4.3, 11.5, true);

  // Passage block with arch hole.
  const w = halfPassage * 2 + 2.4;
  const shape = new THREE.Shape();
  shape.moveTo(-w / 2, 0);
  shape.lineTo(w / 2, 0);
  shape.lineTo(w / 2, passH);
  shape.lineTo(-w / 2, passH);
  shape.closePath();
  const archR = 2.9;
  const archH = 5.6;
  const hole = new THREE.Path();
  hole.moveTo(-archR, 0);
  hole.lineTo(-archR, archH - archR);
  hole.absarc(0, archH - archR, archR, Math.PI, 0, true);
  hole.lineTo(archR, 0);
  hole.closePath();
  shape.holes.push(hole);
  const geo = new THREE.ExtrudeGeometry(shape, { depth: passDepth, bevelEnabled: false });
  geo.translate(0, y0, gz - passDepth / 2);
  ctx.bag.stone.push(geo);

  addCollider(ctx, {
    kind: 'aabb',
    minX: -w / 2,
    maxX: -archR + 0.05,
    minZ: gz - passDepth / 2,
    maxZ: gz + passDepth / 2,
    top: y0 + passH,
    bottom: y0,
  });
  addCollider(ctx, {
    kind: 'aabb',
    minX: archR - 0.05,
    maxX: w / 2,
    minZ: gz - passDepth / 2,
    maxZ: gz + passDepth / 2,
    top: y0 + passH,
    bottom: y0,
  });

  // Portcullis: grid of bars frozen in the raised position overhead.
  const bars: THREE.BufferGeometry[] = [];
  for (let i = -3; i <= 3; i += 1) {
    const bar = new THREE.CylinderGeometry(0.09, 0.09, 4.6, 6);
    bar.translate(i * 0.78, y0 + archH + 2.6, gz);
    bars.push(bar);
  }
  for (let j = 0; j < 5; j += 1) {
    const bar = new THREE.CylinderGeometry(0.07, 0.07, 4.7, 6);
    bar.rotateX(Math.PI / 2);
    bar.rotateZ(Math.PI / 2);
    bar.translate(0, y0 + archH + 0.7 + j * 0.85, gz);
    bars.push(bar);
  }
  const portcullis = mergeGeometries(bars);
  bars.forEach((b) => b.dispose());
  if (portcullis) ctx.bag.wood.push(portcullis);

  // Wall stubs connecting the gatehouse drums into the ring are handled by
  // the circular curtain's gate gap (sized to the gatehouse block).

  // Drawbridge deck across the moat (flat, timber, on visible piles).
  const deckLen = 13.5;
  const deck = new THREE.BoxGeometry(5.6, 0.32, deckLen);
  deck.translate(0, y0 - 0.16, gz + passDepth / 2 + deckLen / 2 - 1.2);
  ctx.bag.wood.push(deck);
  addCollider(ctx, {
    kind: 'aabb',
    minX: -2.8,
    maxX: 2.8,
    minZ: gz + passDepth / 2 - 1.2,
    maxZ: gz + passDepth / 2 + deckLen - 1.2,
    top: y0 - 0.001,
    bottom: y0 - 2,
  });
  for (const px of [-2.2, 0, 2.2]) {
    for (const pz of [gz + passDepth / 2 + 3, gz + passDepth / 2 + 9]) {
      const pile = new THREE.CylinderGeometry(0.26, 0.26, 4.4, 8);
      pile.translate(px, y0 - 2.3, pz);
      ctx.bag.wood.push(pile);
    }
  }
}

/** The keep: battered square donjon, crenellated, four corner turretlings. */
function buildKeep(ctx: BuildCtx): void {
  const y0 = ctx.plateauWY();
  const kx = 0;
  const kz = -21;
  const baseR = 8.6;
  const bodyH = 19;

  const profile: Array<[number, number]> = [
    [baseR + 0.7, 0],
    [baseR + 0.4, 1.4],
    [baseR - 1.1, bodyH - 1.2],
    [baseR - 0.6, bodyH],
    [baseR - 1.5, bodyH + 1.9],
  ];
  const geo = latheProfile(profile, 24);
  geo.translate(kx, y0, kz);
  ctx.bag.stone.push(geo);

  // Crenellated crown ring.
  const crown = latheProfile(
    [
      [baseR - 1.5, 0],
      [baseR - 1.35, 1.0],
    ],
    24,
  );
  crown.translate(kx, y0 + bodyH + 1.9, kz);
  ctx.bag.stone.push(crown);
  const merlons = 22;
  for (let i = 0; i < merlons; i += 1) {
    const a = (i / merlons) * Math.PI * 2;
    const m = new THREE.Matrix4();
    m.makeRotationY(-a);
    m.setPosition(
      kx + Math.cos(a) * (baseR - 1.62),
      y0 + bodyH + 3.45,
      kz + Math.sin(a) * (baseR - 1.62),
    );
    m.multiply(new THREE.Matrix4().makeScale(1.25, 1.1, 0.5));
    ctx.merlonXforms.push(m);
  }

  // Four corner turretlings with cone caps.
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const tx = kx + sx * (baseR - 1.1);
    const tz = kz + sz * (baseR - 1.1);
    const tgeo = latheProfile(
      [
        [2.05, 0],
        [1.86, 3],
        [1.72, bodyH + 1.2],
        [1.98, bodyH + 2.2],
        [1.62, bodyH + 3.6],
      ],
      14,
    );
    tgeo.translate(tx, y0, tz);
    ctx.bag.stone.push(tgeo);
    const cap = new THREE.ConeGeometry(2.15, 3.4, 14);
    cap.translate(tx, y0 + bodyH + 5.3, tz);
    ctx.bag.roof.push(cap);
    addCollider(ctx, { kind: 'cylinder', x: tx, z: tz, r: 1.9, top: y0 + bodyH + 1, bottom: y0 });
  }

  addCollider(ctx, { kind: 'cylinder', x: kx, z: kz, r: baseR - 0.9, top: y0 + bodyH, bottom: y0 });

  // Windows: dark inset planes stacked on the south face (courtyard side).
  for (let f = 0; f < 4; f += 1) {
    for (const wx of [-3.4, 0, 3.4]) {
      const m = new THREE.Matrix4();
      m.setPosition(wx, y0 + 4.4 + f * 3.7, kz + baseR - 0.86);
      m.multiply(new THREE.Matrix4().makeScale(0.85, 1.7, 1));
      ctx.slitXforms.push(m);
    }
  }

  // Hall building against the east curtain: box + gable shingle roof.
  const hx = WALL_HALF - 12.5;
  const hz = 6;
  const hallGeo = new THREE.BoxGeometry(9, 5.4, 22);
  hallGeo.translate(hx, y0 + 2.7, hz);
  ctx.bag.lime.push(hallGeo);
  addCollider(ctx, {
    kind: 'aabb',
    minX: hx - 4.5,
    maxX: hx + 4.5,
    minZ: hz - 11,
    maxZ: hz + 11,
    top: y0 + 5.4,
    bottom: y0,
  });
  const roofShape = new THREE.Shape();
  roofShape.moveTo(-5.4, 0);
  roofShape.lineTo(0, 3.1);
  roofShape.lineTo(5.4, 0);
  roofShape.lineTo(-5.4, 0);
  // Ridge runs along the hall's length (+Z); no rotation — the cross-section
  // is already in XY and the extrusion spans the hall depth.
  const roofGeo = new THREE.ExtrudeGeometry(roofShape, { depth: 23, bevelEnabled: false });
  roofGeo.translate(hx, y0 + 5.4, hz - 11.5);
  ctx.bag.roof.push(roofGeo);

  // Well in the middle of the yard.
  const wellBase = new THREE.CylinderGeometry(1.15, 1.25, 1.1, 14);
  wellBase.translate(-8, y0 + 0.55, 10);
  ctx.bag.stone.push(wellBase);
  addCollider(ctx, { kind: 'cylinder', x: -8, z: 10, r: 1.3, top: y0 + 1.1, bottom: y0 });
  for (const px of [-0.9, 0.9]) {
    const post = new THREE.BoxGeometry(0.18, 2.4, 0.18);
    post.translate(-8 + px, y0 + 2.2, 10);
    ctx.bag.wood.push(post);
  }
  const wellRoof = new THREE.ConeGeometry(1.7, 1.0, 8);
  wellRoof.translate(-8, y0 + 3.9, 10);
  ctx.bag.roof.push(wellRoof);
}

/**
 * Circular curtain: straight extruded segments following a ring, one walkable
 * ring collider for the whole circuit, gate gap at the south (angle 0 = +Z).
 */
function buildRingCurtain(
  ctx: BuildCtx,
  radius: number,
  h: number,
  gapHalfDeg: number,
  segments: number,
): void {
  const y0 = ctx.plateauY;
  for (let i = 0; i < segments; i += 1) {
    const a0 = ((i + 0.5) / segments) * Math.PI * 2;
    const a1 = ((i + 1.5) / segments) * Math.PI * 2;
    const p0: [number, number] = [Math.sin(a0) * radius, Math.cos(a0) * radius];
    const p1: [number, number] = [Math.sin(a1) * radius, Math.cos(a1) * radius];
    // Gap angle from the segment midpoint POSITION: atan2(x, z) is 0 at +Z
    // (south, where the gatehouse stands), ±180 at north.
    const midDeg = (Math.atan2(p0[0] + p1[0], p0[1] + p1[1]) * 180) / Math.PI;
    if (Math.abs(midDeg) < gapHalfDeg) continue; // gate gap
    buildWallRun(ctx, p0, p1, { h, noCollider: true });
  }
  const baseT = h === WALL_H ? WALL_BASE_T : 1.9;
  const topT = h === WALL_H ? WALL_TOP_T : 1.2;
  // Two arc colliders leaving the south gate wedge open (0 = +Z south).
  addCollider(ctx, {
    kind: 'ring',
    x: 0,
    z: 0,
    rIn: radius - topT / 2,
    rOut: radius + baseT / 2,
    aStart: gapHalfDeg,
    aEnd: 180,
    top: y0 + h,
    bottom: y0,
  });
  addCollider(ctx, {
    kind: 'ring',
    x: 0,
    z: 0,
    rIn: radius - topT / 2,
    rOut: radius + baseT / 2,
    aStart: -180,
    aEnd: -gapHalfDeg,
    top: y0 + h,
    bottom: y0,
  });
}

/** Exterior stair from the outer ward up to the wall-walk (west, axis-aligned). */
function buildStairs(ctx: BuildCtx): void {
  const y0 = ctx.plateauWY();
  const steps = Math.round(WALL_H / 0.38);
  const x0 = -21;
  const tread = 0.85;
  for (let i = 0; i <= steps; i += 1) {
    const hgt = ((i + 1) / (steps + 1)) * WALL_H;
    const geo = new THREE.BoxGeometry(tread, hgt, 2.2);
    geo.translate(x0 - i * tread, y0 + hgt / 2, 0);
    ctx.bag.stone.push(geo);
    addCollider(ctx, {
      kind: 'aabb',
      minX: x0 - (i + 1) * tread,
      maxX: x0 - i * tread,
      minZ: -1.1,
      maxZ: 1.1,
      top: y0 + hgt,
      bottom: y0,
    });
  }
}

export async function buildCastle(): Promise<CastleBuild> {
  const group = new THREE.Group();
  group.name = 'castle-keep';
  const bag = new GeometryBag();
  const colliders: Collider[] = [];
  const merlonXforms: THREE.Matrix4[] = [];
  const slitXforms: THREE.Matrix4[] = [];
  const PLATEAU_Y = 9.5;
  const ctx: BuildCtx = {
    bag,
    colliders,
    merlonXforms,
    slitXforms,
    get plateauY() {
      return PLATEAU_Y;
    },
    plateauWY() {
      return PLATEAU_Y;
    },
  };

  // Concentric layout: circular outer curtain matched to the moat ring,
  // inner ward around the keep, gatehouse due south.
  buildRingCurtain(ctx, WALL_HALF, WALL_H, 13, 28);

  // Inner ward wall (lower, its own south gate facing the outer gate).
  buildRingCurtain(ctx, 17, 4.5, 15, 20);

  buildGatehouse(ctx);
  buildKeep(ctx);
  buildStairs(ctx);

  // Ring towers: varied heights, evenly spread, clear of the gate.
  const towerAngles = [40, 78, 118, 152, 208, 242, 282, 320];
  towerAngles.forEach((deg, i) => {
    const a = (deg * Math.PI) / 180;
    const tall = i % 2 === 0;
    buildRoundTower(
      ctx,
      Math.sin(a) * WALL_HALF,
      Math.cos(a) * WALL_HALF,
      tall ? 5.2 : 4.4,
      tall ? 11.5 : 9,
      tall,
    );
  });

  const ashlar = paintAshlar();
  const lime = paintLimewash();
  const shingle = paintShingle();
  const wood = paintWood();

  const stoneMat = createTriplanarMaterial(ashlar.albedo, { scale: 2.6, bumpScale: 0.28 });
  const limeMat = createTriplanarMaterial(lime.albedo, { scale: 3.6, bumpScale: 0.14 });
  const roofMat = createTriplanarMaterial(shingle.albedo, { scale: 2.1, bumpScale: 0.32, roughness: 0.82 });
  const woodMat = createTriplanarMaterial(wood.albedo, { scale: 1.7, bumpScale: 0.22, roughness: 0.85 });

  const meshes: THREE.Mesh[] = [];
  const makeMergedMesh = (
    geos: THREE.BufferGeometry[],
    mat: THREE.Material,
    name: string,
  ): void => {
    if (geos.length === 0) return;
    // Extrude is non-indexed, lathe/box are indexed — normalize to non-indexed
    // so mergeGeometries accepts the mix.
    const uniform = geos.map((g) => (g.index ? g.toNonIndexed() : g));
    const merged = mergeGeometries(uniform, false);
    uniform.forEach((g) => g.dispose());
    geos.forEach((g) => g.dispose());
    if (!merged) return;
    merged.computeVertexNormals();
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = name;
    group.add(mesh);
    meshes.push(mesh);
  };
  makeMergedMesh(bag.stone, stoneMat, 'castle-stone');
  makeMergedMesh(bag.lime, limeMat, 'castle-lime');
  makeMergedMesh(bag.roof, roofMat, 'castle-roof');
  makeMergedMesh(bag.wood, woodMat, 'castle-wood');

  // Instanced merlons + slits.
  if (merlonXforms.length > 0) {
    const merlonGeo = new THREE.BoxGeometry(1, 1, 1);
    const merlonMat = createTriplanarMaterial(ashlar.albedo, { scale: 2.6, bumpScale: 0.28 });
    const inst = new THREE.InstancedMesh(merlonGeo, merlonMat, merlonXforms.length);
    merlonXforms.forEach((m, i) => inst.setMatrixAt(i, m));
    inst.castShadow = true;
    inst.receiveShadow = true;
    inst.name = 'castle-merlons';
    group.add(inst);
    meshes.push(inst);
  }
  if (slitXforms.length > 0) {
    const slitGeo = new THREE.PlaneGeometry(1, 1);
    const slitMat = new THREE.MeshBasicMaterial({ color: 0x14100c });
    const inst = new THREE.InstancedMesh(slitGeo, slitMat, slitXforms.length);
    slitXforms.forEach((m, i) => inst.setMatrixAt(i, m));
    inst.name = 'castle-slits';
    group.add(inst);
    meshes.push(inst);
  }

  return {
    group,
    colliders,
    merlonCount: merlonXforms.length,
    dispose() {
      meshes.forEach((m) => {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      });
      [stoneMat, limeMat, roofMat, woodMat].forEach((m) => m.dispose());
      [ashlar, lime, shingle, wood].forEach((t) => t.albedo.dispose());
    },
  };
}







